'use client';
import { withTimeout } from '@s7/utils';
import { PluginsStatusTracker } from './PluginsStatusTracker/PluginsStatusTracker.js';
import { EventDeferrer } from './EventDeferrer/EventDeferrer.js';

/**
 * Используется для защиты жизненного цикла от повторных вызовов
 * `resolve` / `reject` в возвращаемом Promise.
 *
 * ! Гарантирует, что жизненный цикл плагина не будет нарушен,
 * когда проблемы реализации инструмента аналитики
 * невозможно решить на уровне плагина.
 *
 * !Важно! Невозможно поместить в локальную область видимости.
 * */ const callsGuard = new Map();
/**
 * Отвечает за инициализацию состояния плагинов
 * и передачу событий плагинам для обработки.
 *
 * Если плагин инициализируется асинхронно - создаёт временную очередь событий.
 * Попытка асинхронной инициализации прекращается по таймауту.
 *
 * Порядок вызовов:
 *
 * init().listen();
 * */ const AnalyticsWorker = (options)=>{
    const { bus, plugins, timeout = 5 * 1000 } = options;
    const tracker = PluginsStatusTracker({
        bus
    });
    const deferrer = EventDeferrer({
        bus
    });
    const worker = {
        init
    };
    /**
     * [LIFECYCLE CALL] initialize
     *
     * Отвечает за инициализацию плагина.
     * */ function init() {
        for (const plugin of plugins){
            callsGuard.set(plugin.id, false);
            const evaluator = plugin.initialize();
            const status = evaluator();
            tracker.track(plugin);
            if (status instanceof Promise) {
                withTimeout(status, timeout).then(()=>{
                    if (!callsGuard.get(plugin.id)) {
                        callsGuard.set(plugin.id, true);
                        tracker.activate(plugin);
                        deferrer.dispatchDeferredEvents(plugin);
                    }
                }).catch(()=>{
                    tracker.untrack(plugin);
                    deferrer.eject(plugin);
                });
            } else if (status) {
                tracker.activate(plugin);
            } else {
                tracker.untrack(plugin);
                deferrer.eject(plugin);
            }
        }
        return {
            listen
        };
    }
    /**
     * [LIFECYCLE CALL] onEventEmitted
     * */ function listen() {
        bus.emit('worker-started-listening');
        bus.on('event-added', (eventName, payload)=>{
            for (const plugin of plugins){
                if (!tracker.isTracked(plugin)) {
                    continue;
                }
                if (tracker.isActive(plugin)) {
                    if (deferrer.hasLimitedQueueFor(plugin)) {
                        deferrer.enqueue(plugin, eventName, payload);
                    } else {
                        /*
                         * Во избежание потенциальной блокировки main thread
                         * клиентом аналитики оборачиваем в Promise.
                         * */ new Promise((resolve)=>resolve(plugin.onEventEmitted(eventName, payload))).then(()=>bus.emit('event-sent', eventName, payload)).catch((error)=>bus.emit('event-error', eventName, payload, error));
                    }
                } else {
                    deferrer.enqueue(plugin, eventName, payload);
                }
            }
        });
    }
    return worker;
};

export { AnalyticsWorker };
//# sourceMappingURL=AnalyticsWorker.js.map
